import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import Reveal from "./Reveal";
import Logo from "./Logo";

const FOOTER_LINKS = {
  Studio: [
    { label: "Our Work", to: "/ourwork" },
    { label: "Gallery", to: "/gallery" },
    { label: "About TABA", to: "/about" },
    { label: "The Team", to: "/team" },
  ],
  Practice: [
    { label: "Mural Art", to: "/ourwork#muralart" },
    { label: "Graphic Design", to: "/ourwork#graphicwork" },
    { label: "Sculpture", to: "/ourwork#sculpturework" },
    { label: "Painting", to: "/ourwork#paintingwork" },
  ],
  Contact: [
    { label: "+91 99114 99221", href: "tel:+919911499221" },
    { label: "+91 95606 15712", href: "tel:+919560615712" },
    { label: "tabaartist@gmail.com", href: "mailto:tabaartist@gmail.com" },
  ],
};

const SocialIcon = ({ d }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d={d} />
  </svg>
);

export default function Footer() {
  const canvasRef = useRef(null);

  // Subtle ambient orb in footer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;
    const draw = () => {
      const w = (canvas.width = canvas.offsetWidth);
      const h = (canvas.height = canvas.offsetHeight);
      ctx.clearRect(0, 0, w, h);
      const cx = w * 0.5 + Math.cos(t * 0.0008) * w * 0.15;
      const cy = h * 0.5 + Math.sin(t * 0.0006) * h * 0.15;
      const r = Math.min(w, h) * 0.45;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      grad.addColorStop(0, "rgba(194, 92, 58, 0.55)");
      grad.addColorStop(0.5, "rgba(194, 92, 58, 0.18)");
      grad.addColorStop(1, "rgba(10, 9, 8, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      t += 16;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <footer className="relative bg-[var(--bg)] text-[var(--bone)] overflow-hidden border-t border-[var(--line)]">
      {/* Ambient orb */}
      <canvas
        ref={canvasRef}
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[500px] opacity-90 pointer-events-none"
      />

      {/* Big wordmark */}
      <div className="relative pt-24 md:pt-32 pb-8 px-5 md:px-10">
        <Reveal>
          <h2 className="font-display font-light leading-[0.85] tracking-tight text-[clamp(4rem,18vw,16rem)] text-center select-none">
            <span className="italic font-extralight">art</span>
            <span className="text-[var(--clay)]">·</span>
            <span>on</span>
            <span className="text-[var(--clay)]">·</span>
            <span className="italic font-extralight">walls</span>
          </h2>
        </Reveal>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-[var(--line)] overflow-hidden">
        <div className="marquee-track py-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              {[
                "TABA",
                "Mural Art",
                "Sculpture",
                "Graphic Design",
                "Canvas Painting",
                "Portrait",
                "Studio Practice",
                "Est. 2018",
              ].map((t, j) => (
                <span
                  key={`${i}-${j}`}
                  className="font-display italic text-3xl md:text-5xl text-[var(--bone)] whitespace-nowrap"
                >
                  {t}
                  <span className="mx-8 text-[var(--clay)]">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Top columns */}
      <div className="relative px-5 md:px-10 pt-16 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <Logo size="md" />
                <div>
                  <div className="font-display font-light tracking-[0.18em] uppercase text-[var(--bone)] text-2xl">
                    Taba
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--clay)]">
                    The Art By Artist
                  </div>
                </div>
              </div>
              <p className="text-[var(--muted)] max-w-md text-[15px] leading-relaxed">
                A Delhi-based mural art studio painting bold, large-scale
                stories on walls across India — from cafés and schools to
                flyovers and heritage sites.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
                <span className="w-8 h-px bg-[var(--clay)]" />
                Available for commissions
              </div>
            </Reveal>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {Object.entries(FOOTER_LINKS).map(([title, items]) => (
              <Reveal key={title}>
                <h4 className="text-[10px] uppercase tracking-[0.32em] text-[var(--muted)] mb-5">
                  {title}
                </h4>
                <ul className="space-y-3">
                  {items.map((link) =>
                    link.to ? (
                      <li key={link.label}>
                        <Link
                          to={link.to}
                          className="text-[15px] text-[var(--bone)] hover:text-[var(--clay)] transition-colors link-underline"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ) : (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          className="text-[15px] text-[var(--bone)] hover:text-[var(--clay)] transition-colors link-underline"
                        >
                          {link.label}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </Reveal>
            ))}
          </div>

          {/* Newsletter-ish CTA */}
          <div className="lg:col-span-2">
            <Reveal>
              <h4 className="text-[10px] uppercase tracking-[0.32em] text-[var(--muted)] mb-5">
                Studio
              </h4>
              <p className="text-[15px] leading-relaxed">
                F-10/22 Ground floor, near Moosa Apt., Joga Bai Ext., Jamia Nagar, South Delhi 110025
              </p>
              <Link
                to="/map"
                className="mt-4 inline-flex items-center gap-2 text-[var(--clay)] link-underline text-sm"
              >
                View on map
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </Link>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-[var(--line)] px-5 md:px-10 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--muted)]">
            © {new Date().getFullYear()} TABA · The Art By Artist
          </div>

          <div className="flex items-center gap-3">
            <a
              aria-label="YouTube"
              href="https://www.youtube.com/@TABATheArtByArtist"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 grid place-items-center rounded-full border border-[var(--line)] text-[var(--bone)] hover:border-[var(--clay)] hover:text-[var(--clay)] transition-colors"
            >
              <SocialIcon d="M21.6 7.2c-.2-1.4-1.2-2.4-2.6-2.6C16.4 4.2 12 4.2 12 4.2s-4.4 0-7 .4C3.6 4.8 2.6 5.8 2.4 7.2 2 9.8 2 12 2 12s0 2.2.4 4.8c.2 1.4 1.2 2.4 2.6 2.6 2.6.4 7 .4 7 .4s4.4 0 7-.4c1.4-.2 2.4-1.2 2.6-2.6.4-2.6.4-4.8.4-4.8s0-2.2-.4-4.8zM10 15.5v-7l6 3.5-6 3.5z" />
            </a>
            <a
              aria-label="Instagram"
              href="https://www.instagram.com/taba.co.in?igsh=ODA1NTc5OTg5Nw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 grid place-items-center rounded-full border border-[var(--line)] text-[var(--bone)] hover:border-[var(--clay)] hover:text-[var(--clay)] transition-colors"
            >
              <SocialIcon d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.8.2 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2-.1-1.2-.1-1.6-.1-4.8s0-3.6.1-4.8c.1-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4 1.2-.1 1.6-.1 4.8-.1zm0 1.8c-3.1 0-3.5 0-4.7.1-1.1 0-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c0 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1 0 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c0-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1zm0 3.1a4.9 4.9 0 1 1 0 9.8 4.9 4.9 0 0 1 0-9.8zm0 1.8a3.1 3.1 0 1 0 0 6.2 3.1 3.1 0 0 0 0-6.2zm5.1-2a1.2 1.2 0 1 1 0 2.3 1.2 1.2 0 0 1 0-2.3z" />
            </a>
            <a
              aria-label="WhatsApp"
              href="https://wa.me/919911499221"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 grid place-items-center rounded-full border border-[var(--line)] text-[var(--bone)] hover:border-[var(--clay)] hover:text-[var(--clay)] transition-colors"
            >
              <SocialIcon d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}